import { useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatCost } from "@/utils/costEstimation";
import { CreditCard, CheckCircle } from "lucide-react";
import { toast } from "sonner";

interface PayPalBudgetPurchaseProps {
  onBudgetUpdate?: (newBudget: number) => void;
  currentBudget: number;
}

export function PayPalBudgetPurchase({ onBudgetUpdate, currentBudget }: PayPalBudgetPurchaseProps) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);
  const [processing, setProcessing] = useState(false);
  
  const BUDGET_PACKAGES = [
    { amount: 5, price: 5, label: t("budget.starter"), popular: false },
    { amount: 10, price: 10, label: t("budget.basic"), popular: false },
    { amount: 25, price: 25, label: t("budget.pro"), popular: true },
    { amount: 50, price: 50, label: t("budget.premium"), popular: false },
    { amount: 100, price: 100, label: t("budget.enterprise"), popular: false },
  ];

  const handleApprove = async (data: any, actions: any) => {
    setProcessing(true);
    try {
      const details = await actions.order.capture();
      
      if (details.status === "COMPLETED") {
        const packageData = BUDGET_PACKAGES.find(p => p.price === selectedPackage);
        if (!packageData) return;

        const newBudget = currentBudget + packageData.amount;
        
        const { error } = await supabase
          .from("profiles")
          .update({ budget: newBudget })
          .eq("id", user?.id);

        if (error) throw error;

        toast.success(t("toast.paymentSuccess").replace("{amount}", formatCost(packageData.amount)));
        
        if (onBudgetUpdate) {
          onBudgetUpdate(newBudget);
        }
        
        setSelectedPackage(null);
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(t("toast.paymentFailed"));
    } finally {
      setProcessing(false);
    }
  };

  const paypalClientId = import.meta.env.VITE_PAYPAL_CLIENT_ID || "test";

  if (!selectedPackage) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            {t("budget.purchaseTitle")}
          </CardTitle>
          <CardDescription>
            {t("budget.purchaseDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {BUDGET_PACKAGES.map((pkg) => (
              <Card 
                key={pkg.price}
                className={`relative cursor-pointer transition-all hover:shadow-lg ${
                  pkg.popular ? 'border-primary shadow-md' : ''
                }`}
                onClick={() => setSelectedPackage(pkg.price)}
              >
                {pkg.popular && (
                  <Badge className="absolute -top-2 -right-2 bg-primary text-primary-foreground">
                    {t("budget.popular")}
                  </Badge>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">{pkg.label}</CardTitle>
                  <div className="text-3xl font-bold text-primary">
                    ${pkg.price}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-success" />
                    <span>{formatCost(pkg.amount)} {t("budget.aiBudget")}</span>
                  </div>
                  <Button 
                    variant={pkg.popular ? "default" : "outline"}
                    className="w-full"
                  >
                    {t("budget.selectPackage")}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="mt-6 text-xs text-muted-foreground space-y-1">
            <p>{t("budget.purchaseInfo1")}</p>
            <p>{t("budget.purchaseInfo2")}</p>
            <p>{t("budget.purchaseInfo3")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const selectedPkg = BUDGET_PACKAGES.find(p => p.price === selectedPackage);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("budget.completePayment")}</CardTitle>
        <CardDescription>
          {t("budget.purchasing").replace("{label}", selectedPkg?.label || "").replace("{price}", String(selectedPkg?.price || 0))}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
          <div className="text-sm text-muted-foreground mb-1">{t("budget.youWillReceive")}</div>
          <div className="text-2xl font-bold text-primary">
            {formatCost(selectedPkg?.amount || 0)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {t("budget.addedToBalance")}
          </div>
        </div>

        <PayPalScriptProvider options={{ 
          clientId: paypalClientId,
          currency: "USD"
        }}>
          <PayPalButtons
            createOrder={(data, actions) => {
              return actions.order.create({
                intent: "CAPTURE",
                purchase_units: [
                  {
                    amount: {
                      value: selectedPkg?.price.toString() || "0",
                      currency_code: "USD"
                    },
                    description: `WhatAI Budget - ${selectedPkg?.label} Package`,
                  },
                ],
              });
            }}
            onApprove={handleApprove}
            onError={(err) => {
              console.error("PayPal error:", err);
              toast.error(t("toast.paymentFailed"));
              setProcessing(false);
            }}
            disabled={processing}
          />
        </PayPalScriptProvider>

        <Button
          variant="outline"
          className="w-full"
          onClick={() => setSelectedPackage(null)}
          disabled={processing}
        >
          {t("budget.backToPackages")}
        </Button>
      </CardContent>
    </Card>
  );
}
